import { useState } from 'react';
import type { FormEvent } from 'react';
import { Link } from 'react-router-dom';
import {
  componerTelefono,
  DIAL_POR_DEFECTO,
  edadEn,
  esquemaRegistro,
  GENERO,
  GENEROS,
  LARGO_MINIMO_CONTRASENA,
  PAIS_POR_DEFECTO,
  type RespuestaRegistro,
} from '@copathy/shared';
import { api, ErrorApi } from '../lib/api';
import { Boton } from '../componentes/Boton';
import { Campo } from '../componentes/Campo';
import { Marca } from '../componentes/Marca';
import { PanelPresentacion } from '../componentes/PanelPresentacion';
import { SelectorPais, SelectorTelefono } from '../componentes/CamposPais';
import { Combo } from '../componentes/Combo';
import { Candado, Correo, Escudo, Llave, Ojo, OjoTachado, Usuario } from '../componentes/iconos';

/**
 * Registro de un trabajador (D-022).
 *
 * El código de la empresa va PRIMERO y solo: es lo único que la persona puede
 * no tener a mano, y descubrirlo después de llenar seis campos es la forma más
 * rápida de perder a alguien.
 */
export function Registro() {
  const [campos, setCampos] = useState({
    codigoEmpresa: '',
    nombre: '',
    correo: '',
    contrasena: '',
    cargo: '',
    area: '',
    fechaNacimiento: '',
    estadoRegion: '',
    ciudad: '',
  });
  /*
    Fuera de `campos` porque no son `<input>` de texto: el teléfono son dos piezas
    y los países son combos. Meterlos en el mismo objeto obligaría a que `poner`
    —que espera un evento de input— sirviera para tres formas distintas de cambio.
  */
  const [dial, setDial] = useState(DIAL_POR_DEFECTO);
  const [telefono, setTelefono] = useState('');
  const [genero, setGenero] = useState<string | null>(null);
  const [nacionalidad, setNacionalidad] = useState<string | null>(PAIS_POR_DEFECTO);
  const [paisResidencia, setPaisResidencia] = useState<string | null>(PAIS_POR_DEFECTO);
  const [verClave, setVerClave] = useState(false);
  const [errores, setErrores] = useState<Record<string, string>>({});
  const [errorGeneral, setErrorGeneral] = useState<string | null>(null);
  const [enviando, setEnviando] = useState(false);
  const [listo, setListo] = useState<RespuestaRegistro | null>(null);

  /* `edadEn` vive en shared: la misma función que valida el rango 16-100 en el
     contrato es la que pinta el número acá. Dos cuentas de «cuántos años tiene»
     que difieran en un día alrededor del cumpleaños serían un rechazo
     inexplicable desde la pantalla. */
  const edad = campos.fechaNacimiento ? edadEn(campos.fechaNacimiento) : null;

  const poner = (clave: keyof typeof campos) => (e: { target: { value: string } }) =>
    setCampos((c) => ({ ...c, [clave]: e.target.value }));

  async function enviar(e: FormEvent) {
    e.preventDefault();
    setErrores({});
    setErrorGeneral(null);

    /*
      ⚠️ **El mismo `esquemaRegistro` que valida el servidor.** Cuando los datos de
      persona pasaron a obligatorios (D-087) esta pantalla se rompió sola: el
      esquema empezó a exigir seis campos que el formulario no pedía, y el
      `safeParse` fallaba con errores de campos que no existían en la pantalla.
      Ese es el comportamiento correcto de una sola definición compartida — el
      contrato no se puede cambiar de un lado y olvidar el otro.
    */
    const r = esquemaRegistro.safeParse({
      ...campos,
      area: campos.area || undefined,
      telefono: componerTelefono(dial, telefono),
      genero: genero ?? undefined,
      nacionalidad: nacionalidad ?? undefined,
      paisResidencia: paisResidencia ?? undefined,
    });

    if (!r.success) {
      const malos: Record<string, string> = {};
      for (const issue of r.error.issues) {
        const clave = String(issue.path[0] ?? '_');
        malos[clave] ??= issue.message;
      }
      setErrores(malos);
      return;
    }

    setEnviando(true);
    try {
      setListo(await api.post<RespuestaRegistro>('/api/auth/registro', r.data));
    } catch (err) {
      if (err instanceof ErrorApi) {
        if (err.campos) setErrores(err.campos);
        else setErrorGeneral(err.message);
      } else {
        setErrorGeneral('No pudimos conectar con el servidor. Revisa tu conexión.');
      }
    } finally {
      setEnviando(false);
    }
  }

  return (
    <div className="flex min-h-screen bg-white">
      <PanelPresentacion />

      <main className="flex min-w-0 flex-1 items-center justify-center bg-white px-6 py-12">
        <div className="w-full max-w-[22rem]">
          <div className="mb-10 lg:hidden">
            <Marca tamano="md" />
          </div>

          {listo ? (
            <div>
              <div className="mb-5 flex h-12 w-12 items-center justify-center rounded-2xl bg-naranja-50 text-naranja-600">
                <Correo size={24} />
              </div>
              <h1 className="text-[1.75rem] leading-tight font-bold text-marino-800">
                Revisa tu correo
              </h1>
              <p className="mt-3 text-sm leading-relaxed text-marino-500">
                Te enviamos un enlace a <strong className="text-marino-800">{campos.correo}</strong>{' '}
                para confirmar que es tuyo. Vence en 72 horas.
              </p>

              <div className="mt-6 rounded-2xl border border-marino-100 bg-marino-50/60 p-5">
                <h2 className="text-sm font-bold text-marino-800">Y después, ¿qué?</h2>
                <ol className="mt-3 space-y-2.5 text-sm text-marino-500">
                  <li>
                    <strong className="text-marino-700">1.</strong> Confirmas tu correo con ese
                    enlace.
                  </li>
                  <li>
                    <strong className="text-marino-700">2.</strong> Copathy revisa tu solicitud
                    para <strong className="text-marino-700">{listo.empresaNombre}</strong>.
                  </li>
                  <li>
                    <strong className="text-marino-700">3.</strong> Te avisamos por correo y ya
                    puedes entrar.
                  </li>
                </ol>
                {/* Se dice antes, no cuando intente entrar y no pueda. */}
                <p className="mt-4 text-xs leading-relaxed text-marino-400">
                  Hasta que aprueben tu solicitud no vas a poder iniciar sesión, aunque hayas
                  confirmado el correo.
                </p>
              </div>

              {listo.enlaceDev && (
                <div className="mt-5 rounded-xl border border-amber-200 bg-amber-50 p-4 text-xs text-amber-900">
                  <strong>Solo en desarrollo:</strong> todavía no hay servidor de correo, así
                  que el enlace se muestra acá.
                  <a href={listo.enlaceDev} className="mt-2 block break-all underline">
                    {listo.enlaceDev}
                  </a>
                </div>
              )}

              <Link
                to="/entrar"
                className="mt-7 inline-block text-sm font-medium text-marino-500 hover:text-naranja-600"
              >
                Volver a ingresar
              </Link>
            </div>
          ) : (
            <>
              <h1 className="text-[1.75rem] leading-tight font-bold text-marino-800">
                Crea tu cuenta
              </h1>
              <p className="mt-2 text-sm text-marino-400">
                Necesitas el código que te dio tu empresa.
              </p>

              <form onSubmit={enviar} noValidate className="mt-7 space-y-4">
                <Campo
                  etiqueta="Código de tu empresa"
                  autoFocus
                  autoComplete="off"
                  autoCapitalize="characters"
                  icono={<Llave size={18} />}
                  value={campos.codigoEmpresa}
                  onChange={poner('codigoEmpresa')}
                  error={errores.codigoEmpresa}
                />

                <Campo
                  etiqueta="Nombre completo"
                  autoComplete="name"
                  icono={<Usuario size={18} />}
                  value={campos.nombre}
                  onChange={poner('nombre')}
                  error={errores.nombre}
                />

                <Campo
                  etiqueta="Correo electrónico"
                  type="email"
                  autoComplete="email"
                  inputMode="email"
                  icono={<Correo size={18} />}
                  value={campos.correo}
                  onChange={poner('correo')}
                  error={errores.correo}
                />

                <Campo
                  etiqueta="Contraseña"
                  type={verClave ? 'text' : 'password'}
                  autoComplete="new-password"
                  icono={<Candado size={18} />}
                  value={campos.contrasena}
                  onChange={poner('contrasena')}
                  error={errores.contrasena}
                  derecha={
                    <button
                      type="button"
                      onClick={() => setVerClave((v) => !v)}
                      aria-label={verClave ? 'Ocultar contraseña' : 'Ver contraseña'}
                      className="rounded-full p-2.5 text-marino-400 transition hover:bg-marino-50 hover:text-marino-700"
                    >
                      {verClave ? <OjoTachado size={18} /> : <Ojo size={18} />}
                    </button>
                  }
                />
                {!errores.contrasena && (
                  <p className="-mt-2 pl-5 text-xs text-marino-400">
                    Mínimo {LARGO_MINIMO_CONTRASENA} caracteres. Una frase que recuerdes sirve
                    mejor que algo corto y raro.
                  </p>
                )}

                <div className="grid grid-cols-2 gap-3">
                  <Campo
                    etiqueta="Cargo"
                    value={campos.cargo}
                    onChange={poner('cargo')}
                    error={errores.cargo}
                  />
                  <Campo
                    etiqueta="Área (opcional)"
                    value={campos.area}
                    onChange={poner('area')}
                    error={errores.area}
                  />
                </div>

                <SelectorTelefono
                  etiqueta="Teléfono"
                  dial={dial}
                  numero={telefono}
                  alCambiar={(d, n) => {
                    setDial(d);
                    setTelefono(n);
                  }}
                />
                {errores.telefono && (
                  <p className="-mt-2 pl-5 text-xs font-medium text-red-600">{errores.telefono}</p>
                )}

                <div className="grid grid-cols-2 gap-3">
                  {/*
                    `type="date"` y no tres campos como en la app: acá hay teclado
                    físico y el selector nativo del navegador de escritorio es bueno
                    —escribir la fecha también funciona—. En un teléfono el nativo
                    arranca en hoy y hay que girar cuarenta años, y por eso la app
                    tiene su propio control.
                  */}
                  <Campo
                    etiqueta="Fecha de nacimiento"
                    type="date"
                    autoComplete="bday"
                    value={campos.fechaNacimiento}
                    onChange={poner('fechaNacimiento')}
                    error={errores.fechaNacimiento}
                  />
                  <Combo
                    etiqueta="Género"
                    opciones={GENEROS.map((g) => ({ clave: g, texto: GENERO[g] }))}
                    valor={genero}
                    alCambiar={(v) => setGenero(v === null ? null : String(v))}
                    vacio="Elige una opción"
                    sinCoincidencias="Sin coincidencias."
                  />
                </div>
                {/*
                  La edad calculada, debajo de la fecha. Es idea de Jonathan y es el
                  control que de verdad funciona: **el número que uno sabe de memoria
                  es su edad, no su fecha en ISO**. Una fecha mal puesta no se nota
                  mirándola; «49 años» cuando tienes 34 salta sola.
                */}
                {!errores.fechaNacimiento && edad !== null && (
                  <p className="-mt-2 pl-5 text-xs text-marino-500">
                    <strong className="font-bold text-marino-700">{edad} años</strong> · si no
                    cuadra, corrige la fecha
                  </p>
                )}
                {errores.genero && (
                  <p className="-mt-2 pl-5 text-xs font-medium text-red-600">{errores.genero}</p>
                )}

                <div className="grid grid-cols-2 gap-3">
                  <SelectorPais
                    etiqueta="Nacionalidad"
                    iso={nacionalidad}
                    alCambiar={setNacionalidad}
                  />
                  <SelectorPais
                    etiqueta="País donde vives"
                    iso={paisResidencia}
                    alCambiar={setPaisResidencia}
                  />
                </div>

                <div className="grid grid-cols-2 gap-3">
                  <Campo
                    etiqueta="Estado o provincia"
                    value={campos.estadoRegion}
                    onChange={poner('estadoRegion')}
                    error={errores.estadoRegion}
                  />
                  <Campo
                    etiqueta="Ciudad"
                    value={campos.ciudad}
                    onChange={poner('ciudad')}
                    error={errores.ciudad}
                  />
                </div>

                {errorGeneral && (
                  <p role="alert" className="text-sm font-medium text-red-600">
                    {errorGeneral}
                  </p>
                )}

                <div className="flex gap-3 rounded-2xl border border-marino-100 bg-marino-50/60 p-4">
                  <span className="mt-0.5 shrink-0 text-naranja-500">
                    <Escudo size={20} />
                  </span>
                  <p className="text-xs leading-relaxed text-marino-500">
                    Tus respuestas a los cuestionarios son confidenciales. Tu empresa ve los
                    resultados del grupo, nunca lo que respondiste tú.
                  </p>
                </div>

                <Boton type="submit" cargando={enviando} className="w-full">
                  {enviando ? 'Enviando…' : 'Crear cuenta'}
                </Boton>
              </form>

              <p className="mt-6 text-center text-sm text-marino-400">
                ¿Ya tienes cuenta?{' '}
                <Link to="/entrar" className="font-medium text-marino-600 hover:text-naranja-600">
                  Ingresar
                </Link>
              </p>
            </>
          )}
        </div>
      </main>
    </div>
  );
}
